import { useId, useMemo, useState } from "react";

import type { Recipient } from "../api/types";
import { initials } from "../lib/format";

export interface UserPickerValue {
  query: string;
  selectedID: string;
}

interface UserPickerProps {
  label: string;
  placeholder?: string;
  value: UserPickerValue;
  recipients: Recipient[];
  loadStatus: "loading" | "ready" | "error";
  loadErrorMessage: string | null;
  loadingMessage?: string;
  emptyMessage?: string;
  noSelectionHint?: string;
  required?: boolean;
  onChange: (next: UserPickerValue) => void;
}

export function UserPicker({
  label,
  placeholder,
  value,
  recipients,
  loadStatus,
  loadErrorMessage,
  loadingMessage,
  emptyMessage,
  noSelectionHint,
  required,
  onChange
}: UserPickerProps) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const inputID = useId();
  const listboxID = useId();

  const matches = useMemo(
    () => filterRecipients(recipients, value.query),
    [recipients, value.query]
  );

  function pick(recipient: Recipient) {
    // Use just the display label for the input value — emails inside angle
    // brackets read as a free-text typo target. The selectedID is what the
    // form submits; the input string is purely a label.
    onChange({ query: recipientDisplayLabel(recipient), selectedID: recipient.id });
    setDropdownOpen(false);
    setHighlightedIndex(0);
  }

  function handleQueryChange(event: React.ChangeEvent<HTMLInputElement>) {
    onChange({ query: event.target.value, selectedID: "" });
    setDropdownOpen(true);
    setHighlightedIndex(0);
  }

  function handleQueryKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setDropdownOpen(true);
      setHighlightedIndex((index) => Math.min(matches.length - 1, index + 1));
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightedIndex((index) => Math.max(0, index - 1));
      return;
    }
    if (event.key === "Enter" && dropdownOpen && matches.length > 0) {
      event.preventDefault();
      const target = matches[Math.min(highlightedIndex, matches.length - 1)];
      if (target !== undefined) {
        pick(target);
      }
      return;
    }
    // Tab accepts the highlighted suggestion but lets focus continue to the
    // next field — without this, keyboard users tab into Amount with
    // selectedID still empty and Send sits silently disabled.
    if (event.key === "Tab" && dropdownOpen && matches.length > 0 && value.query.trim() !== "") {
      const target = matches[Math.min(highlightedIndex, matches.length - 1)];
      if (target !== undefined) {
        pick(target);
      }
      return;
    }
    if (event.key === "Escape" && dropdownOpen) {
      event.preventDefault();
      // Stop the native event so a modal-level document Escape listener
      // doesn't also close the modal on the same keystroke.
      event.nativeEvent.stopImmediatePropagation();
      setDropdownOpen(false);
    }
  }

  const hasSelection = value.selectedID !== "";

  return (
    <div className="form-row typeahead">
      <label htmlFor={inputID}>{label}</label>
      <div className={`typeahead__field${hasSelection ? " is-selected" : ""}`}>
        <input
          id={inputID}
          type="text"
          autoComplete="off"
          role="combobox"
          aria-expanded={dropdownOpen}
          aria-controls={listboxID}
          aria-autocomplete="list"
          value={value.query}
          onChange={handleQueryChange}
          onKeyDown={handleQueryKeyDown}
          onFocus={() => setDropdownOpen(true)}
          onBlur={() => {
            // Delay so a click on a list item still registers before the
            // dropdown closes.
            window.setTimeout(() => setDropdownOpen(false), 120);
          }}
          placeholder={placeholder}
          required={required}
        />
        {hasSelection && (
          <button
            type="button"
            className="typeahead__clear"
            aria-label="Clear selection"
            onClick={() => onChange({ query: "", selectedID: "" })}
          >
            ×
          </button>
        )}
      </div>
      {loadStatus === "loading" && loadingMessage !== undefined && (
        <small>{loadingMessage}</small>
      )}
      {loadStatus === "error" && (
        <small className="form-row__error">{loadErrorMessage}</small>
      )}
      {loadStatus === "ready" && recipients.length === 0 && emptyMessage !== undefined && (
        <small>{emptyMessage}</small>
      )}
      {dropdownOpen && matches.length > 0 && (
        <ul className="typeahead__menu" role="listbox" id={listboxID}>
          {matches.map((recipient, index) => {
            const isHighlighted = index === Math.min(highlightedIndex, matches.length - 1);
            return (
              <li
                key={recipient.id}
                role="option"
                aria-selected={isHighlighted}
                className={`typeahead__option${isHighlighted ? " is-highlighted" : ""}`}
                onMouseDown={(event) => {
                  // mousedown beats blur so we keep focus on the input
                  // and the click registers.
                  event.preventDefault();
                  pick(recipient);
                }}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                {recipient.avatar_url !== "" ? (
                  <img
                    className="typeahead__avatar typeahead__avatar--img"
                    src={recipient.avatar_url}
                    alt=""
                    aria-hidden="true"
                  />
                ) : (
                  <span className="typeahead__avatar" aria-hidden="true">
                    {initials(recipient.name, recipient.email)}
                  </span>
                )}
                <span className="typeahead__option-text">
                  <span className="typeahead__name">
                    {recipient.name === "" ? recipient.email : recipient.name}
                  </span>
                  {recipient.name !== "" && (
                    <span className="typeahead__email">{recipient.email}</span>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {dropdownOpen
        && value.query.trim() !== ""
        && matches.length === 0
        && loadStatus === "ready" && (
          <small className="form-row__error">No matches.</small>
        )}
      {/* While the menu is open it already says "pick one" — showing the hint
          too just grows the row under the caret. Nudge only after blur. */}
      {!dropdownOpen
        && value.query.trim() !== ""
        && value.selectedID === ""
        && matches.length > 0
        && noSelectionHint !== undefined && (
          <small>{noSelectionHint}</small>
        )}
    </div>
  );
}

// filterRecipients narrows the visible list by case-insensitive substring on
// either name or email. Empty query returns the full list so the dropdown
// works as a "browse" affordance too.
function filterRecipients(recipients: Recipient[], query: string): Recipient[] {
  const trimmed = query.trim().toLowerCase();
  if (trimmed === "") {
    return recipients;
  }
  return recipients.filter((recipient) => {
    const haystack = `${recipient.name} ${recipient.email}`.toLowerCase();
    return haystack.includes(trimmed);
  });
}

function recipientDisplayLabel(recipient: Recipient): string {
  return recipient.name.trim() === "" ? recipient.email : recipient.name;
}
